import { useState } from 'react';
import { Container, Divider, Typography } from '@mui/material';

import DetailItem from 'components/Staff/ItemDetail';
import Button from 'components/Common/Button';
import Modal from 'components/Common/Modal';

import { findValuesByKeys, getDateAndTime } from 'utils/helpers';
import { useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import { notify } from 'components/Common/Toast';
import { useDispatch } from 'react-redux';
import { updateBasketStatus } from 'redux/slices/baskets/givingBasketSilce';
import { AppDispatch } from 'redux/store';
import { BASKET_STATUS_LABELS, isSystemBasket } from 'types/baskets';
// The General Fund is always ACTIVE and cannot be suspended, closed or given a scheduled
// transition — the backend 409s all three, so the controls are hidden rather than shown broken.
interface BasketDetailProp {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing prop signature
    basketDetails?: any;
}

function BasketDetailContainer({ basketDetails }: BasketDetailProp) {
    const { name, subType, description, nextPlannedStatus, nextPlannedStatusAt, followUpSuspensionAt, company, status } =
        basketDetails || {};
    const { profileItems } = company || {};
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
    const navigate = useNavigate();
    const keys = ['TELEPHONE'];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const attributes: any = findValuesByKeys(profileItems || [], keys) ?? {};
    const { TELEPHONE } = attributes;
    const dispatch = useDispatch<AppDispatch>();
    const toggleBasketStatus = async () => {
        const { id } = basketDetails;
        try {
            setIsLoading(true);
            const _status = status === 'SUSPEND' ? 'ACTIVE' : 'SUSPEND';
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing dispatched thunk payload union
            const response: any = await dispatch(updateBasketStatus({ status: _status, id }));
            if (!response?.error) {
                if (status === 'SUSPEND') {
                    notify('Basket reactivated successfully!', 'success');
                } else {
                    notify('Basket suspended successfully!', 'success');
                }
                navigate(routes.home.main);
            }
        } catch (error) {
            console.log('SuspendBasket error', error);
        } finally {
            setIsLoading(false);
            setConfirmModalOpen(false);
        }
    };

    return (
        <Container>
            <Container sx={{ p: 3, mt: 2 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 400, mb: 1 }}>Basket Details</Typography>
                <Divider />
                <div style={{ display: 'flex', justifyContent: 'flex-start', flexDirection: 'column' }}>
                    <DetailItem labelWidth={'15%'} label={'Title'} value={name} />
                    <DetailItem labelWidth={'15%'} label={'Status'} value={(status && BASKET_STATUS_LABELS[status]) || status || ''} />
                    <DetailItem labelWidth={'15%'} label={'Type'} value={subType} />
                    <DetailItem labelWidth={'15%'} label={'Contact'} value={TELEPHONE ?? ''} />
                    {/* Source of truth is nextPlannedStatus/nextPlannedStatusAt/followUpSuspensionAt —
                        never startDate, which is informational metadata only. */}
                    {nextPlannedStatus === 'ACTIVE' && nextPlannedStatusAt && (
                        <DetailItem labelWidth={'15%'} label={'Activates on'} value={getDateAndTime(nextPlannedStatusAt)} />
                    )}
                    {nextPlannedStatus === 'ACTIVE' && followUpSuspensionAt && (
                        <DetailItem labelWidth={'15%'} label={'Then suspends on'} value={getDateAndTime(followUpSuspensionAt)} />
                    )}
                    {nextPlannedStatus === 'SUSPENDED' && nextPlannedStatusAt && (
                        <DetailItem labelWidth={'15%'} label={'Suspends on'} value={getDateAndTime(nextPlannedStatusAt)} />
                    )}
                    <DetailItem labelWidth={'15%'} label={'Details'} value={description} />
                </div>
                {!(status === 'PENDING' || status === 'CLOSED') &&
                    (isSystemBasket(basketDetails) ? (
                        <Typography variant="caption" display="block" sx={{ color: 'text.secondary', textAlign: 'center', mt: 2 }}>
                            Parish general fund — always active. It cannot be suspended or deleted.
                        </Typography>
                    ) : (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                            <Button
                                title={status === 'SUSPEND' ? 'Reactivate Basket' : 'Suspend Basket'}
                                loading={isLoading}
                                onClick={() => setConfirmModalOpen(true)}
                                sx={{ width: { xs: '60%' } }}
                            />
                        </div>
                    ))}
            </Container>
            <Modal
                onClose={() => setConfirmModalOpen(false)}
                isOpen={confirmModalOpen}
                modalTitle={status === 'SUSPEND' ? 'Confirm Reactivate' : 'Confirm Suspend'}
            >
                <>
                    <Typography sx={{ fontSize: 18, my: 2, color: '#1F2937', fontWeight: 400 }}>
                        {status === 'SUSPEND'
                            ? 'Are you sure you want to reactivate this basket?'
                            : 'Are you sure you want to suspend this basket? It will be removed from every donor’s app until reactivated.'}
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button backgroundColor="white" onClick={() => setConfirmModalOpen(false)} width="45%" title="No" />
                        <Button loading={isLoading} onClick={toggleBasketStatus} width="45%" title="Yes" />
                    </div>
                </>
            </Modal>
        </Container>
    );
}

export default BasketDetailContainer;
